/**
 * Editor Integration — asset, font, placeholder and theme resolution.
 *
 * Everything the editor needs resolved BEFORE it opens: image sources pointed
 * at their final home, fonts collected so they can be preloaded, placeholders
 * inventoried so they can be filled, and the design's tokens expressed as an
 * editor theme.
 *
 * Pure and deterministic. The asset resolver is injected, which is the seam a
 * future CDN plugs into without touching this layer or the editor.
 */

import { sanitizeAssetUrl } from '../../../utils/sanitize';
import { META_NAMESPACE, EDITOR_DEFAULTS } from './sessionSchema';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isStr = (v) => typeof v === 'string' && v.length > 0;

/** The default asset resolver: sources are already final. */
export const identityAssetResolver = (src) => src;

/**
 * Resolve every image source through `resolveAsset`, then sanitize the result.
 *
 * Image sources are model-controlled: a hallucinated or hostile `javascript:`
 * or `data:text/html` URL must never reach an editor `src`. Sanitization runs
 * LAST, so a resolver cannot reintroduce an unsafe scheme, and it is applied
 * here rather than at the call site so no caller can forget it. Rejected URLs
 * become an empty slot the user can fill — never a partially-cleaned URL.
 *
 * @returns {{ pages: Object[], assets: Object[] }}
 */
export const resolveAssets = (pages, resolveAsset = identityAssetResolver) => {
  const assets = [];
  const resolved = pages.map((page) => ({
    ...page,
    elements: page.elements.map((el) => {
      if (!isStr(el.src) && !isStr(el.image)) return el;
      const field = isStr(el.src) ? 'src' : 'image';
      const original = el[field];
      const next = sanitizeAssetUrl(resolveAsset(original, el));
      assets.push({ elementId: el.id, pageId: page.id, original, resolved: next, rejected: next === null });
      return next === original ? el : { ...el, [field]: next };
    }),
  }));
  return { pages: resolved, assets };
};

/**
 * Sanitize every page background.
 *
 * The editor reads `page.background` straight into its canvas background, so a
 * model-supplied `javascript:` URL would land in a CSS url(). A background that
 * carries no scheme (a colour, a gradient) is left exactly as it is.
 */
export const resolveBackgrounds = (pages) => pages.map((page) => {
  const bg = page.background;
  if (!isStr(bg) || !/^[a-z][a-z0-9+.-]*:/i.test(bg)) return page;
  const safe = sanitizeAssetUrl(bg);
  return safe === bg ? page : { ...page, background: safe || EDITOR_DEFAULTS.background };
});

/**
 * Every font family the document actually uses, so the editor's existing font
 * loader can preload them. A text element with no family uses the editor's own
 * default, which needs no preloading.
 */
export const resolveFonts = (pages) => {
  const families = new Set();
  pages.forEach((page) => page.elements.forEach((el) => {
    if (el.type === 'text' && isStr(el.fontFamily)) families.add(el.fontFamily);
  }));
  return [...families].sort(); // sorted so the session is deterministic
};

/**
 * Image slots still waiting for a picture: the renderer left the editor's
 * native frame drop-zone plus the intent behind it.
 */
export const resolveImagePlaceholders = (pages) => {
  const placeholders = [];
  pages.forEach((page) => page.elements.forEach((el) => {
    const meta = isObj(el[META_NAMESPACE]) ? el[META_NAMESPACE] : {};
    const empty = el.type === 'frame' && !isStr(el.image);
    if (!empty && !isStr(meta.imageQuery)) return;
    if (!empty) return; // a filled image keeps its query for reference, but is not a slot
    placeholders.push({
      elementId: el.id,
      pageId: page.id,
      query: meta.imageQuery || null,
      role: meta.role || null,
      fit: el.fit || 'cover',
      swappable: isObj(meta.editable) ? meta.editable.swappable !== false : true,
    });
  }));
  return placeholders;
};

/**
 * The design's tokens as an editor theme: the palette, type styles and the
 * page background config the editor's background panel reads.
 */
export const resolveTheme = (document, pages) => {
  const metadata = isObj(document.metadata) ? document.metadata : {};
  const tokens = isObj(metadata.tokens) ? metadata.tokens : {};
  const first = pages[0] || {};
  const raw = isStr(first.background) ? first.background : EDITOR_DEFAULTS.background;
  // A background carrying a URL ends up in a CSS url(); it is model-controlled,
  // so an unsafe scheme falls back to the editor's default rather than through.
  const looksLikeUrl = /^[a-z][a-z0-9+.-]*:/i.test(raw);
  const background = looksLikeUrl ? (sanitizeAssetUrl(raw) || EDITOR_DEFAULTS.background) : raw;
  const isImage = /^(https?:|data:|blob:)/.test(background);
  const isGradient = background.includes('gradient(');

  return {
    colors: isObj(tokens.color) ? { ...tokens.color } : {},
    type: isObj(tokens.type) ? { ...tokens.type } : {},
    space: isObj(tokens.space) ? { ...tokens.space } : {},
    radius: isObj(tokens.radius) ? { ...tokens.radius } : {},
    genome: metadata.genome || null,
    // The editor's own background model, so its panel opens on the right state.
    backgroundConfig: {
      type: isImage ? 'image' : (isGradient ? 'gradient' : 'color'),
      color: isImage || isGradient ? EDITOR_DEFAULTS.background : background,
      ...(isImage ? { image: background } : {}),
      ...(isGradient ? { gradient: background } : {}),
      size: 'cover',
      position: 'center center',
      opacity: 1,
      blur: 0,
    },
  };
};

/** Attach the resolved background config to each page, as the editor reads it. */
export const applyTheme = (pages, theme) => pages.map((page, i) => (
  i === 0 && !isObj(page.backgroundConfig) ? { ...page, backgroundConfig: theme.backgroundConfig } : page
));
